import { Checkbox } from '@lemon-io/ui-kit';
import { Box, styled, Typography } from '@mui/material';

import InfoIcon from '@internal/components/app/components/info-icon';
import OfferStatusBanner from '@internal/components/app/components/offer-status-banner';

const MAIN_COLUMN_WIDTH = 845;
const SIDE_COLUMN_WIDTH = 275;
const COLUMNS_GAP = 20;
const PAGE_PADDING = 40;
export const APPLY_DRAWER_WIDTH = 'clamp(381px, 30vw, 560px)';
const PEEK_MIN_WIDTH = 'clamp(380px, 32vw, 560px)';
const SIDE_TRACK_MIN_WIDTH = `max(${SIDE_COLUMN_WIDTH}px, calc(${PEEK_MIN_WIDTH} - ${
  COLUMNS_GAP + PAGE_PADDING
}px))`;

export const PageRoot = styled(Box, {
  shouldForwardProp: prop => prop !== '$public',
})<{ $public?: boolean }>(({ theme, $public }) => ({
  position: 'relative',
  minHeight: '100vh',
  backgroundColor: theme.palette.main.grey100,
  padding: theme.spacing(3, 2),

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(5.25, `${PAGE_PADDING}px`, 8),
  },

  ...($public && {
    [theme.breakpoints.up('lg')]: {
      paddingLeft: 228,
    },
  }),
}));

export const PublicLogoBar = styled('header')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(3),

  [theme.breakpoints.up('lg')]: {
    position: 'absolute',
    top: 38,
    left: 24,
    marginBottom: 0,
  },
}));

export const PageColumn = styled(Box)({
  width: '100%',
});

export const PageGrid = styled(Box, {
  shouldForwardProp: prop => prop !== '$withSide',
})<{ $withSide: boolean }>(({ theme, $withSide }) => ({
  display: 'grid',
  gridTemplateColumns: `minmax(0, ${MAIN_COLUMN_WIDTH}px) minmax(${SIDE_TRACK_MIN_WIDTH}, 1fr)`,
  gridTemplateAreas: $withSide
    ? '"header ." "main side"'
    : '"header ." "main ."',
  columnGap: COLUMNS_GAP,
  rowGap: theme.spacing(3),
  alignItems: 'start',

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: `minmax(0, ${MAIN_COLUMN_WIDTH}px)`,
    gridTemplateAreas: $withSide ? '"header" "side" "main"' : '"header" "main"',
  },
}));

export const HeaderArea = styled(Box)({
  gridArea: 'header',
  minWidth: 0,
});

export const ProjectId = styled(Typography)({
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 'normal',
  color: '#808080',
});

export const MetaRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: theme.spacing(2),
  marginTop: theme.spacing(0.5),
}));

export const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

export const HeaderChip = styled(Box)({
  display: 'flex',
  flexShrink: 0,
  marginTop: 8,
});

export const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: 32,
  fontWeight: 450,
  lineHeight: '46px',
  letterSpacing: '-0.5px',
  color: theme.palette.main.black,
  overflowWrap: 'anywhere',
}));

export const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 'normal',
  color: theme.palette.main.black,
}));

export const PageStatusBanner = styled(OfferStatusBanner)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

export const DirectHireBanner = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  padding: theme.spacing(1.5),
  borderRadius: 6,
  backgroundColor: theme.palette.main.grey100,
}));

export const DirectHireTitle = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.main.black,
}));

export const DirectHireBody = styled(Typography)({
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.5,
  color: '#808080',
});

export const MainColumn = styled(Box)(({ theme }) => ({
  gridArea: 'main',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

export const SideColumn = styled(Box)(({ theme }) => ({
  gridArea: 'side',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),

  [theme.breakpoints.up('lg')]: {
    width: SIDE_COLUMN_WIDTH,
  },
}));

export const Card = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.main.white,
  borderRadius: 6,
  padding: theme.spacing(3),
}));

export const SectionCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

export const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 450,
  lineHeight: '24px',
  color: theme.palette.main.black,
}));

export const BasicInfoGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(3, 0),
}));

export const BasicInfoColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: 280,
  maxWidth: '100%',
}));

export const BasicInfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  fontSize: 14,
  lineHeight: 'normal',
  color: theme.palette.main.black,
}));

export const BasicInfoLabel = styled('span')({
  color: '#808080',
});

export const SkillRows = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}));

export const SkillRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

export const SkillRowLabel = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '19px',
  color: theme.palette.main.black,
  whiteSpace: 'nowrap',
}));

export const PillList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
  minWidth: 0,
}));

export const SkillPill = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: 29,
  backgroundColor: '#EFEFEF',
  color: theme.palette.main.black,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '15px',
  maxWidth: '100%',
  overflowWrap: 'anywhere',
}));

export const BodyText = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.5,
  color: theme.palette.main.black,
  whiteSpace: 'pre-line',
  overflowWrap: 'anywhere',
}));

export const ClientGoalLabel = styled('span')({
  fontWeight: 600,
});

export const SideCardText = styled(Typography)({
  fontSize: 12,
  fontWeight: 400,
  color: '#808080',
});

export const SideCardActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  marginTop: theme.spacing(0.5),

  '& > *': { width: '100%' },
}));

export const SignInHint = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(0, 3),
  fontSize: 12,
  fontWeight: 400,
  color: '#808080',
  textAlign: 'center',
}));

export const SignInLink = styled('button')(({ theme }) => ({
  padding: 0,
  border: 'none',
  background: 'transparent',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  color: theme.palette.main.black,
  textDecoration: 'underline',
  cursor: 'pointer',
}));

export const SummaryLink = styled('button')(({ theme }) => ({
  padding: 0,
  border: 'none',
  background: 'transparent',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 'normal',
  color: theme.palette.main.black,
  textDecoration: 'none',
  textUnderlineOffset: 4,
  whiteSpace: 'nowrap',
  cursor: 'pointer',

  '&:hover, &:focus-visible': {
    textDecoration: 'underline',
  },
}));

export const MatcherLabel = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 400,
  color: theme.palette.main.black,
}));

export const MatcherName = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  color: theme.palette.main.black,
}));

export const ContactButton = styled('a')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  padding: '8px 16px',
  borderRadius: 4,
  border: '1px solid #EFEFEF',
  backgroundColor: theme.palette.main.white,
  color: theme.palette.main.black,
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 'normal',
  textDecoration: 'none',
  cursor: 'pointer',

  '&:hover': { backgroundColor: theme.palette.main.grey50 },
}));

export const OutlineActionButton = styled('button')(({ theme }) => ({
  flexShrink: 0,
  padding: '8px 16px',
  borderRadius: 4,
  border: '1px solid #EFEFEF',
  backgroundColor: theme.palette.main.white,
  color: theme.palette.main.black,
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 'normal',
  whiteSpace: 'nowrap',
  cursor: 'pointer',

  '&:hover': { backgroundColor: theme.palette.main.grey50 },

  '&:disabled': {
    cursor: 'default',
    opacity: 0.4,
  },
}));

export const BlackActionButton = styled('button')(({ theme }) => ({
  flexShrink: 0,
  padding: '8px 16px',
  borderRadius: 4,
  border: 'none',
  backgroundColor: theme.palette.main.black,
  color: theme.palette.main.white,
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 'normal',
  whiteSpace: 'nowrap',
  cursor: 'pointer',

  '&:disabled': {
    cursor: 'default',
    opacity: 0.4,
  },
}));

export const DeclineDialogBody = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: 14,
  lineHeight: 1.5,
  color: theme.palette.text.secondary75,
}));

export const DeclineCommentField = styled('textarea')(({ theme }) => ({
  width: '100%',
  minHeight: 96,
  marginTop: theme.spacing(2),
  padding: '10px 12px',
  borderRadius: 6,
  border: `1px solid ${theme.palette.main.grey200}`,
  backgroundColor: theme.palette.main.white,
  color: theme.palette.main.black,
  fontFamily: 'inherit',
  fontSize: 14,
  lineHeight: 1.5,
  resize: 'vertical',
  outline: 'none',

  '&:focus': { borderColor: theme.palette.main.black },
  '&::placeholder': { color: theme.palette.text.secondary50 },
}));

export const DeclineDialogFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.25),
  marginTop: theme.spacing(2.5),
}));

export const DrawerFormColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}));

export const FieldAnchor = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

export const ApplyDrawerDescription = styled(Typography)({
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.5,
  color: '#808080',
});

export const SubmitButton = styled(BlackActionButton)({
  flex: 1,
});

export const FormCardTitle = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 500,
  lineHeight: 'normal',
  color: theme.palette.main.black,
}));

export const FieldsColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),

  '&& .MuiInputLabel-root': {
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    overflowWrap: 'anywhere',
    flexWrap: 'wrap',
    lineHeight: 1.35,
  },
}));

export const FieldGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
}));

export const FieldGroupLabel = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 'normal',
  color: theme.palette.main.black,
}));

export const RateFieldsColumn = styled(FieldsColumn)({
  marginTop: 9,
});

export const FieldsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),

  '& > *': { flex: 1, minWidth: 0 },

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

export const InfoBanner = styled(Box, {
  shouldForwardProp: prop => prop !== '$belowTitle',
})<{ $belowTitle?: boolean }>(({ theme, $belowTitle }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  marginTop: $belowTitle ? theme.spacing(2) : 0,
  padding: theme.spacing(1.5),
  borderRadius: 6,
  border: '1px solid #B9E7FF',
  backgroundColor: '#F0FAFF',
}));

export const InfoBannerIcon = styled(InfoIcon)({
  flexShrink: 0,
});

export const InfoBannerText = styled(Typography)(({ theme }) => ({
  minWidth: 0,
  fontSize: 13,
  fontWeight: 500,
  lineHeight: '18px',
  color: theme.palette.main.black,
}));

export const INFO_BANNER_ICON_COLOR = '#0368A3';

export const AgreeCheckbox = styled(Checkbox, {
  shouldForwardProp: prop => prop !== '$error',
})<{ $error?: boolean }>(({ theme, $error }) => ({
  '& .MuiTypography-root': {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 'normal',
    color: theme.palette.main.black,
  },

  ...($error && {
    '& .MuiCheckbox-root': { color: theme.palette.error.main },
  }),
}));

export const AgreeError = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  fontSize: 12,
  lineHeight: 'normal',
  color: theme.palette.error.main,
}));

export const DialogHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

export const DialogHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "'Suisse Intl', 'Inter', sans-serif",
  fontSize: '14px !important',
  fontWeight: 450,
  lineHeight: '24px',
  color: theme.palette.text.primary,
}));

export const DialogCloseButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  padding: 0,
  cursor: 'pointer',
  color: theme.palette.main.black,

  '& svg': { fontSize: 18 },
}));

export const NumberList = styled('ol')(({ theme }) => ({
  margin: 0,
  paddingLeft: 20,
  fontSize: 14,
  lineHeight: 1.7,
  color: theme.palette.main.black,

  '& li + li': { marginTop: theme.spacing(1.5) },
}));

export const ProgressNote = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 10,
  padding: theme.spacing(0, 0.5),
  color: '#808080',

  '& > svg': { flexShrink: 0, marginTop: 3 },
}));

export const ProgressNoteText = styled(Typography)({
  fontSize: 12,
  lineHeight: 1.5,
  color: 'inherit',
});
